import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";

export function Field({ label, required, children, testId, hint }) {
    return (
        <div data-testid={testId ? `${testId}-field` : undefined} className="space-y-2">
            <Label className="text-sm font-medium text-slate-900">
                {label} {required && <span className="text-slate-400">*</span>}
            </Label>
            {children}
            {hint && <p className="text-xs text-slate-500">{hint}</p>}
        </div>
    );
}

export function TextField({ label, required, value, onChange, type = "text", testId, placeholder }) {
    return (
        <Field label={label} required={required} testId={testId}>
            <Input
                data-testid={testId}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="bg-white border-slate-200 h-11"
                required={required}
            />
        </Field>
    );
}

export function TextAreaField({ label, required, value, onChange, testId, placeholder, rows = 4 }) {
    return (
        <Field label={label} required={required} testId={testId}>
            <Textarea
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={rows}
                className="bg-white border-slate-200 resize-none"
                required={required}
            />
        </Field>
    );
}

export function SelectField({ label, required, value, onChange, options, testId, placeholder }) {
    return (
        <Field label={label} required={required} testId={testId}>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger
                    data-testid={testId}
                    className="bg-white border-slate-200 h-11"
                >
                    <SelectValue placeholder={placeholder || "Select..."} />
                </SelectTrigger>
                <SelectContent>
                    {options.map((opt) => (
                        <SelectItem
                            key={opt}
                            value={opt}
                            data-testid={`${testId}-option-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {opt}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </Field>
    );
}

export function CheckboxGroupField({ label, required, values, onChange, options, testId }) {
    const toggle = (opt) => {
        if (values.includes(opt)) {
            onChange(values.filter((v) => v !== opt));
        } else {
            onChange([...values, opt]);
        }
    };
    return (
        <Field label={label} required={required} testId={testId}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {options.map((opt) => {
                    const id = `${testId}-${opt.toLowerCase().replace(/[\s&/]+/g, "-")}`;
                    const checked = values.includes(opt);
                    return (
                        <label
                            key={opt}
                            htmlFor={id}
                            className={`flex items-center gap-3 px-3.5 py-3 rounded-lg border cursor-pointer transition-colors ${
                                checked
                                    ? "border-slate-900 bg-slate-50"
                                    : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                        >
                            <Checkbox
                                id={id}
                                data-testid={id}
                                checked={checked}
                                onCheckedChange={() => toggle(opt)}
                            />
                            <span className="text-sm text-slate-800">{opt}</span>
                        </label>
                    );
                })}
            </div>
        </Field>
    );
}

export function RadioField({ label, required, value, onChange, options, testId }) {
    return (
        <Field label={label} required={required} testId={testId}>
            <RadioGroup
                value={value}
                onValueChange={onChange}
                className="grid grid-cols-2 sm:grid-cols-4 gap-2.5"
            >
                {options.map((opt) => {
                    const id = `${testId}-${opt.toLowerCase().replace(/[\s/]+/g, "-")}`;
                    const checked = value === opt;
                    return (
                        <label
                            key={opt}
                            htmlFor={id}
                            className={`flex items-center gap-3 px-3.5 py-3 rounded-lg border cursor-pointer transition-colors ${
                                checked
                                    ? "border-slate-900 bg-slate-50"
                                    : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                        >
                            <RadioGroupItem id={id} data-testid={id} value={opt} />
                            <span className="text-sm text-slate-800">{opt}</span>
                        </label>
                    );
                })}
            </RadioGroup>
        </Field>
    );
}
